import { useMemo, useState } from 'react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const statusStyles = {
  available: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  blocked: { chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  booked: { chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};

const buildMonthCells = (anchor) => {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(year, month, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const formatMonthLabel = (date) =>
  date.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

const dateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const defaultCurrencyFormatter = (value, currency = 'INR') => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  } catch (_) {
    const prefix = currency && currency.length <= 4 ? currency : '₹';
    return `${prefix} ${num.toLocaleString('en-IN')}`;
  }
};

const isBetween = (iso, start, end) => {
  if (!start || !end) return false;
  const from = start < end ? start : end;
  const to = end > start ? end : start;
  return iso >= from && iso <= to;
};

const dayStatus = (day = {}) => {
  if (day.status) {
    const value = day.status.toString().toLowerCase();
    if (value === 'open') return 'available';
    return value;
  }
  if (day.booked || day.booking_id) return 'booked';
  if (day.available === false || day.is_available === false) return 'blocked';
  return 'available';
};

const AvailabilityCalendar = ({
  month,
  monthsToShow = 1,
  days = [],
  loading = false,
  onMonthChange,
  onDayClick,
  onRangeSelect,
  onQuickAction,
  selectedRange = null,
  currency = 'INR',
  priceFormatter,
}) => {
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const todayIso = useMemo(() => dateKey(new Date()), []);
  const formatPrice = useMemo(
    () => (val) => (priceFormatter ? priceFormatter(val) : defaultCurrencyFormatter(val, currency)),
    [priceFormatter, currency]
  );

  const dayMap = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);
  const selection = selectedRange && selectedRange.from && selectedRange.to ? selectedRange : null;

  const handleMouseDown = (iso) => {
    setDragStart(iso);
    setDragEnd(iso);
  };

  const handleMouseEnter = (iso) => {
    if (dragStart) setDragEnd(iso);
  };

  const handleMouseUp = (iso) => {
    if (!dragStart) return;
    const from = dragStart < iso ? dragStart : iso;
    const to = dragStart > iso ? dragStart : iso;
    onRangeSelect?.({ from, to });
    setDragStart(null);
    setDragEnd(null);
  };

  const renderMonth = (idx) => {
    const anchor = new Date(month.getFullYear(), month.getMonth() + idx, 1);
    const cells = buildMonthCells(anchor);
    const showNav = idx === 0;
    return (
      <div
        key={`${anchor.getFullYear()}-${anchor.getMonth()}`}
        className="flex-1 rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="text-sm font-semibold text-slate-900">{formatMonthLabel(anchor)}</div>
          {showNav && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => onMonthChange?.(-1)}
              >
                Prev
              </button>
              <button
                type="button"
                className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                onClick={() => onMonthChange?.(1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 px-4 py-2 text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Open
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Booked
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-rose-500" /> Blocked
          </span>
        </div>
        <div className="grid grid-cols-7 gap-1 px-3 pt-3 text-center text-[11px] font-semibold text-slate-500">
          {DAY_LABELS.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2 px-3 pb-4">
          {cells.map((cell, idx2) => {
            if (!cell) return <div key={`empty-${idx2}`} className="h-28" />;
            const iso = dateKey(cell);
            const day = dayMap.get(iso) || {};
            const status = dayStatus(day);
            const selected =
              isBetween(iso, dragStart, dragEnd) || (selection && isBetween(iso, selection.from, selection.to));
            const styles = statusStyles[status] || statusStyles.available;
            const isPast = iso < todayIso;
            const basePrice =
              day.base_price ??
              day.base_price_per_night ??
              day.price ??
              day.price_per_night ??
              day.base_weekday_price ??
              null;
            const price = day.price_override ?? day.price ?? basePrice ?? '';
            const effectivePrice = day.effective_price ?? price;
            const minNights = day.min_nights_override ?? day.min_nights ?? day.minNights;
            const hasOverride =
              day.hasOverride ||
              (day.price_override !== undefined && day.price_override !== null) ||
              (day.min_nights_override !== undefined && day.min_nights_override !== null);
            return (
              <button
                key={iso}
                type="button"
                className={[
                  'flex h-28 flex-col rounded-xl border border-slate-200 bg-white p-2 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md',
                  selected ? 'ring-2 ring-indigo-400 border-indigo-200 bg-indigo-50/80' : '',
                  loading ? 'animate-pulse' : '',
                  status === 'booked' ? 'cursor-not-allowed' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={() => handleMouseDown(iso)}
                onMouseEnter={() => handleMouseEnter(iso)}
                onMouseUp={() => handleMouseUp(iso)}
                onClick={() => !isPast && onDayClick?.(iso, day)}
                disabled={isPast}
              >
                <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                  <span>{cell.getDate()}</span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${styles.chip}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${styles.dot}`} />
                    {status === 'booked' ? 'Booked' : status === 'blocked' ? 'Blocked' : 'Open'}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500">{DAY_LABELS[cell.getDay()]}</div>
                <div className="mt-1 text-base font-semibold leading-tight text-slate-900">
                  {formatPrice(effectivePrice) || '-'}
                </div>
                {effectivePrice !== price && price ? (
                  <div className="text-[11px] text-slate-500">Base {formatPrice(price)}</div>
                ) : null}
                {hasOverride && basePrice && (
                  <div className="text-[11px] text-slate-500">Base {formatPrice(basePrice)}</div>
                )}
                {minNights ? <div className="text-[11px] text-slate-500">Min {minNights} nights</div> : null}
                {Array.isArray(day.effective_labels) && day.effective_labels.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                    {day.effective_labels.map((lbl) => (
                      <span
                        key={lbl.name || lbl.type}
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
                        title={lbl.name || lbl.type}
                        style={lbl.color ? { backgroundColor: lbl.color, color: '#0f172a' } : {}}
                      >
                        {lbl.name || lbl.type}
                      </span>
                    ))}
                  </div>
                ) : null}
                {day.note ? (
                  <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                    <span className="truncate rounded-full bg-slate-50 px-2 py-0.5 text-slate-600" title={day.note}>
                      {day.note}
                    </span>
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">Availability calendar</div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => onQuickAction?.('block')}
          >
            Block
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => onQuickAction?.('unblock')}
          >
            Unblock
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => onQuickAction?.('price')}
          >
            Set price
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => onQuickAction?.('min_nights')}
          >
            Set min nights
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            onClick={() => onQuickAction?.('clear')}
          >
            Clear overrides
          </button>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1">
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => onMonthChange?.(-1)}
            >
              Prev
            </button>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => onMonthChange?.(1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        {Array.from({ length: monthsToShow }).map((_, idx) => renderMonth(idx))}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;

